/**
 * TextAnimationSelector Component
 *
 * UI for selecting text animation styles for e-commerce video captions.
 * Follows the same pattern as BackgroundSelector for consistency.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Zap, Type, Move, Sparkles, Waves } from 'lucide-react';

// Text animation type definitions
export const TEXT_ANIMATIONS = {
  typewriter: {
    id: 'typewriter',
    name: 'Typewriter',
    description: 'Character by character typing',
    icon: Type,
    tags: ['classic', 'authentic', 'storytelling'],
  },
  word_reveal: {
    id: 'word_reveal',
    name: 'Word Reveal',
    description: 'Words appear with spring physics',
    icon: Sparkles,
    tags: ['elegant', 'premium', 'smooth'],
  },
  blur_fade: {
    id: 'blur_fade',
    name: 'Blur Fade',
    description: 'Words blur to sharp',
    icon: Sparkles,
    tags: ['modern', 'clean', 'professional'],
  },
  slide_up: {
    id: 'slide_up',
    name: 'Slide Up',
    description: 'Words slide up from below',
    icon: Move,
    tags: ['energetic', 'dynamic', 'sales'],
  },
  fade_scale: {
    id: 'fade_scale',
    name: 'Fade Scale',
    description: 'Words fade in with scale',
    icon: Sparkles,
    tags: ['subtle', 'minimal', 'elegant'],
  },
  bounce: {
    id: 'bounce',
    name: 'Bounce',
    description: 'Words bounce in playfully',
    icon: Waves,
    tags: ['playful', 'fun', 'gen-z'],
  },
  wave: {
    id: 'wave',
    name: 'Wave',
    description: 'Wave-like entrance',
    icon: Waves,
    tags: ['creative', 'artistic', 'unique'],
  },
  flip: {
    id: 'flip',
    name: 'Flip',
    description: '3D flip animation',
    icon: Move,
    tags: ['tech', 'modern', 'impressive'],
  },
};

// Animation type button
const AnimationTypeButton = ({ animation, isSelected, onClick }) => {
  const Icon = animation.icon;

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-3 p-3 rounded-lg border-2 transition-all w-full text-left
        ${isSelected
          ? 'border-purple-500 bg-purple-50'
          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
        }
      `}
    >
      <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}`}>
        <Icon className={`w-5 h-5 ${isSelected ? 'text-purple-600' : 'text-gray-500'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
            {animation.name}
          </span>
          {isSelected && <Check className="w-4 h-4 text-purple-500" />}
        </div>
        <p className="text-xs text-gray-500 truncate">{animation.description}</p>
      </div>
    </button>
  );
};

/**
 * Main TextAnimationSelector Component
 */
export const TextAnimationSelector = ({
  textAnimation,
  onTextAnimationChange,
  llmSuggested,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!compact);

  const selectedAnimation = TEXT_ANIMATIONS[textAnimation] || TEXT_ANIMATIONS.typewriter;

  // Compact mode - just shows current selection with expand button
  if (compact && !isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 hover:border-purple-300 transition-all"
      >
        <div className="flex items-center gap-3">
          <selectedAnimation.icon className="w-5 h-5 text-purple-600" />
          <span className="font-medium text-gray-900">{selectedAnimation.name}</span>
        </div>
        <ChevronDown className="w-5 h-5 text-gray-400" />
      </button>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with collapse button (in compact mode) */}
      {compact && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {t('modal.product.textAnimation', { defaultValue: 'Text Animation' })}
          </span>
          <button
            onClick={() => setIsExpanded(false)}
            className="text-xs text-purple-600 hover:text-purple-800"
          >
            {t('common.collapse', { defaultValue: 'Collapse' })}
          </button>
        </div>
      )}

      {/* Animation Type Selection - Grid layout for compactness */}
      <div className="grid grid-cols-2 gap-2">
        {Object.values(TEXT_ANIMATIONS).map((animation) => (
          <AnimationTypeButton
            key={animation.id}
            animation={animation}
            isSelected={textAnimation === animation.id}
            onClick={() => onTextAnimationChange(animation.id)}
          />
        ))}
      </div>

      {/* LLM Suggestion indicator */}
      {llmSuggested?.textAnimation && llmSuggested.textAnimation !== textAnimation && (
        <button
          onClick={() => onTextAnimationChange(llmSuggested.textAnimation)}
          className="flex items-center gap-2 text-xs text-purple-600 hover:text-purple-800 mt-2"
        >
          <Zap className="w-3 h-3" />
          {t('modal.product.useAiSuggestion', { defaultValue: 'Use AI suggestion' })}:
          {TEXT_ANIMATIONS[llmSuggested.textAnimation]?.name}
        </button>
      )}
    </div>
  );
};

export default TextAnimationSelector;
